<script setup lang="ts">
import { computed } from 'vue'
import { TeamType, TeamStatus, AssignmentStatus } from '@/types'
import type { Personnel, Team } from '@/types'
import { usePersonnelStore } from '@/stores/personnel'
import { useTeamsStore } from '@/stores/teams'
import { useAssignmentsStore } from '@/stores/assignments'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'

const props = defineProps<{
  team: Team | null
  incidentId: string
  opId: string
}>()

const emit = defineEmits<{ debrief: [] }>()

const visible = defineModel<boolean>('visible', { default: false })

const personnelStore = usePersonnelStore()
const teamsStore = useTeamsStore()
const assignmentsStore = useAssignmentsStore()

const teamMembers = computed<Personnel[]>(() => {
  if (!props.team) return []
  return personnelStore.roster.filter((p) => p.team_id === props.team!.team_id)
})

const teamLeader = computed<Personnel | null>(() => {
  if (!props.team?.team_leader_personnel_id) return null
  return personnelStore.roster.find((p) => p.personnel_id === props.team!.team_leader_personnel_id) ?? null
})

const nonLeaderMembers = computed<Personnel[]>(() =>
  teamMembers.value.filter((p) => p.personnel_id !== props.team?.team_leader_personnel_id),
)

const taWithAssignment = computed(() => {
  if (!props.team) return []
  return assignmentsStore.teamAssignments
    .filter((ta) => ta.team_id === props.team!.team_id)
    .flatMap((ta) => {
      const assignment = assignmentsStore.assignments.find((a) => a.assignment_id === ta.assignment_id)
      return assignment ? [{ ta, assignment }] : []
    })
})

const activeAssignments = computed(() =>
  taWithAssignment.value.filter(
    ({ assignment: a }) =>
      a.assignment_status === AssignmentStatus.InProgress ||
      a.assignment_status === AssignmentStatus.NotStarted,
  ),
)

const completedAssignments = computed(() =>
  taWithAssignment.value.filter(({ assignment: a }) => a.assignment_status === AssignmentStatus.Completed),
)

const debriefedCount = computed(() =>
  taWithAssignment.value.filter(({ ta }) => Object.keys(ta.debrief_cols ?? {}).length > 0).length,
)
const allDebriefed = computed(() =>
  taWithAssignment.value.length > 0 && debriefedCount.value === taWithAssignment.value.length,
)

function getOverheadRole(personnelId: string): string | null {
  if (!props.team) return null
  const uo = teamsStore.userOps.find(
    (u) => u.personnel_id === personnelId && u.team_id === props.team!.team_id && u.active,
  )
  return uo?.role ?? null
}

function getQuals(p: Personnel): string[] {
  const q = p.qualifications
  const result: string[] = []
  if (q.medical_qual && q.medical_qual !== 'None') result.push(q.medical_qual as string)
  if (q.searcher_type && q.searcher_type !== 'None') result.push(q.searcher_type as string)
  if (q.rope_qual && q.rope_qual !== 'None') result.push(q.rope_qual as string)
  if (Array.isArray(q.misc_quals)) result.push(...(q.misc_quals as string[]))
  return result
}

const typeLabel: Record<TeamType, string> = {
  [TeamType.Ground]: 'Ground',
  [TeamType.K9]: 'K9',
  [TeamType.Air]: 'Air',
  [TeamType.Bike]: 'Bike',
  [TeamType.Type1]: 'Type 1',
  [TeamType.Rescue]: 'Rescue',
  [TeamType.Overhead]: 'Overhead',
}

const statusLabel: Record<TeamStatus, string> = {
  [TeamStatus.Building]: 'Building',
  [TeamStatus.AwaitingAssignment]: 'Awaiting Assignment',
  [TeamStatus.Assigned]: 'Assigned',
  [TeamStatus.Enroute]: 'Enroute',
  [TeamStatus.OnScene]: 'In Assignment',
  [TeamStatus.RTB]: 'RTB',
  [TeamStatus.Returned]: 'Returned',
  [TeamStatus.Emergency]: 'Emergency',
}

const statusBadgeClass = computed(() => {
  if (!props.team) return ''
  const colors: Record<string, string> = {
    [TeamStatus.Building]: 'bg-surface-container text-outline border-border',
    [TeamStatus.AwaitingAssignment]: 'bg-surface-container text-on-surface-variant border-border',
    [TeamStatus.Assigned]: 'bg-blue-50 text-blue-700 border-blue-200',
    [TeamStatus.Enroute]: 'bg-blue-50 text-blue-700 border-blue-200',
    [TeamStatus.OnScene]: 'bg-green-50 text-green-700 border-green-200',
    [TeamStatus.RTB]: 'bg-amber-50 text-amber-700 border-amber-200',
    [TeamStatus.Returned]: 'bg-surface-container text-outline border-border',
    [TeamStatus.Emergency]: 'bg-red-50 text-red-700 border-red-300',
  }
  return `inline-flex items-center px-2.5 py-0.5 text-xs font-mono border ${colors[props.team.team_status] ?? 'bg-surface-container text-outline border-border'}`
})
</script>

<template>
  <FosDialog
    :open="visible"
    max-width="max-w-2xl"
    @update:open="visible = $event"
  >
    <template #title>
      <div class="flex items-center gap-3 flex-wrap">
        <span class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">
          {{ team?.team_identifier ?? '' }}
        </span>
        <span
          v-if="team"
          class="inline-flex items-center px-2.5 py-0.5 text-xs font-mono border bg-surface-container text-on-surface-variant border-border"
        >
          {{ typeLabel[team.team_type] }}
        </span>
        <span v-if="team" :class="statusBadgeClass">
          {{ statusLabel[team.team_status] }}
        </span>
      </div>
    </template>

    <div v-if="team" class="flex flex-col gap-6 pt-1">

      <!-- Summary -->
      <div>
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">Summary</p>
        <div class="bg-surface-container-low p-3 grid grid-cols-3 gap-x-6 gap-y-3">
          <div>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-0.5">Type</p>
            <p class="text-on-surface font-mono text-sm">{{ typeLabel[team.team_type] }}</p>
          </div>
          <div>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-0.5">Status</p>
            <p class="text-on-surface font-mono text-sm">{{ statusLabel[team.team_status] }}</p>
          </div>
          <div>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-0.5">Size</p>
            <p class="text-on-surface font-mono text-sm">{{ teamMembers.length }} personnel</p>
          </div>
        </div>
      </div>

      <!-- Team Leader -->
      <div>
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">Team Leader</p>
        <div class="bg-surface-container-low p-3">
          <template v-if="teamLeader">
            <div class="flex items-start gap-4" :class="teamLeader.time_out !== null ? 'opacity-50' : ''">
              <div class="flex-1 min-w-0">
                <p class="font-mono text-sm text-on-surface flex items-center gap-1.5">
                  {{ teamLeader.last }}, {{ teamLeader.first }}
                  <span v-if="teamLeader.time_out !== null" class="text-[10px] font-mono uppercase tracking-wide text-outline">out</span>
                </p>
                <p v-if="teamLeader.agency" class="font-mono text-xs text-outline mt-0.5">{{ teamLeader.agency }}</p>
              </div>
              <div v-if="getQuals(teamLeader).length > 0" class="flex flex-wrap gap-1 justify-end">
                <span
                  v-for="q in getQuals(teamLeader)"
                  :key="q"
                  class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-surface-container border border-border text-on-surface-variant"
                >
                  {{ q }}
                </span>
              </div>
            </div>
          </template>
          <p v-else class="font-mono text-sm text-outline">—</p>
        </div>
      </div>

      <!-- Members -->
      <div v-if="nonLeaderMembers.length > 0 || team.team_type !== TeamType.Overhead">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">
          Members <span class="normal-case font-normal">({{ nonLeaderMembers.length }})</span>
        </p>
        <div class="bg-surface-container-low divide-y divide-outline-variant/15">
          <template v-if="nonLeaderMembers.length > 0">
            <div
              v-for="member in nonLeaderMembers"
              :key="member.personnel_id"
              class="flex items-center gap-4 px-3 py-2"
              :class="member.time_out !== null ? 'opacity-50' : ''"
            >
              <div class="flex-1 min-w-0">
                <p class="font-mono text-sm text-on-surface flex items-center gap-1.5">
                  {{ member.last }}, {{ member.first }}
                  <span v-if="member.time_out !== null" class="text-[10px] font-mono uppercase tracking-wide text-outline">out</span>
                </p>
                <p v-if="member.agency" class="font-mono text-xs text-outline mt-0.5">{{ member.agency }}</p>
              </div>
              <!-- Overhead: show role label -->
              <template v-if="team.team_type === TeamType.Overhead">
                <span
                  v-if="getOverheadRole(member.personnel_id)"
                  class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-surface-container border border-border text-on-surface-variant"
                >
                  {{ getOverheadRole(member.personnel_id) }}
                </span>
              </template>
              <!-- Field: show qual chips -->
              <template v-else>
                <div v-if="getQuals(member).length > 0" class="flex flex-wrap gap-1 justify-end">
                  <span
                    v-for="q in getQuals(member)"
                    :key="q"
                    class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-surface-container border border-border text-on-surface-variant"
                  >
                    {{ q }}
                  </span>
                </div>
              </template>
            </div>
          </template>
          <p v-else class="px-3 py-3 font-mono text-sm text-outline">No members assigned</p>
        </div>
      </div>

      <!-- Active Assignments -->
      <div v-if="activeAssignments.length > 0">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">Active Assignments</p>
        <div class="bg-surface-container-low divide-y divide-outline-variant/15">
          <div
            v-for="{ ta: _ta, assignment: a } in activeAssignments"
            :key="a.assignment_id"
            class="flex items-center justify-between gap-4 px-3 py-2"
          >
            <p class="font-mono text-sm text-on-surface">{{ a.assignment_name }}</p>
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs font-mono border',
                a.assignment_status === AssignmentStatus.InProgress
                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-border bg-surface-container text-outline',
              ]"
            >
              {{ a.assignment_status === AssignmentStatus.InProgress ? 'In Progress' : 'Not Started' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Completed Assignments -->
      <div v-if="completedAssignments.length > 0">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest mb-3">
          Completed Assignments <span class="normal-case font-normal">({{ completedAssignments.length }})</span>
        </p>
        <div class="bg-surface-container-low divide-y divide-outline-variant/15">
          <div
            v-for="{ ta, assignment: a } in completedAssignments"
            :key="a.assignment_id"
            class="flex items-center justify-between gap-4 px-3 py-2"
          >
            <p class="font-mono text-sm text-outline">{{ a.assignment_name }}</p>
            <span
              v-if="Object.keys(ta.debrief_cols ?? {}).length > 0"
              class="inline-flex items-center gap-1 text-xs font-mono text-primary"
            >
              <span class="material-symbols-outlined text-[14px]">check_circle</span>
              Debriefed
            </span>
            <span v-else class="text-[10px] font-label uppercase tracking-widest text-outline">Not debriefed</span>
          </div>
        </div>
      </div>

    </div>

    <template #footer>
      <div v-if="taWithAssignment.length > 0" class="flex justify-end">
        <FosButton
          :variant="allDebriefed ? 'tertiary' : 'secondary'"
          size="sm"
          @click="emit('debrief'); visible = false"
        >
          <span v-if="allDebriefed" class="material-symbols-outlined text-[14px]">check_circle</span>
          <span v-else-if="debriefedCount > 0" class="font-mono text-[10px]">{{ debriefedCount }}/{{ taWithAssignment.length }}</span>
          {{ allDebriefed ? 'Debriefed' : 'Debrief' }}
        </FosButton>
      </div>
    </template>
  </FosDialog>
</template>
